/**
 * Claims Service (MIRA 2.0)
 *
 * Claims-API'et er camelCase → mapperne er næsten identitet, men typed +
 * media-normalisering (liste returnerer media som tal ELLER array).
 * Mappere + multipart-builder er eksporteret for unit-tests.
 */

import { apiGet, apiPost, apiPostForm } from './api';
import type {
  Claim, ClaimItem, ClaimType, ClaimStatus,
  PreferredOutcome, ClaimOutcome, ProductHandling, Product, ClaimDraft,
  CustomerMatch, OrderLookupResult,
} from '../types/claims.types';

// ---- Raw shapes (camelCase as returned by MIRA 2.0) ----

interface RawClaimItem {
  id: number | null;
  sku: string;
  productName: string;
  quantity: number;
  claimType: ClaimType;
  description: string | null;
  orderNumber: string | null;
  isB2c: boolean;
  b2cCustomerName?: string | null;
  b2cMobileNumber?: string | null;
  media?: number | unknown[] | null;
}

interface RawClaim {
  id: number;
  status: ClaimStatus;
  source: string;
  salonName: string;
  createdByName: string;
  preferredOutcome: PreferredOutcome;
  outcome: ClaimOutcome | null;
  productHandling?: ProductHandling | null;
  creditNoteNumber?: string | null;
  processedByName?: string | null;
  processedAt?: string | null;
  createdAt: string;
  items: RawClaimItem[];
}

interface RawProduct { sku: string; productName: string; category: string; imageUrl?: string | null; }

// ---- Mappers (exported for tests) ----

function normalizeMedia(media: RawClaimItem['media']): number {
  if (Array.isArray(media)) return media.length;
  return typeof media === 'number' ? media : 0;
}

export function mapClaimItem(raw: RawClaimItem): ClaimItem {
  return {
    id: raw.id ?? null,
    sku: raw.sku,
    productName: raw.productName,
    quantity: raw.quantity,
    claimType: raw.claimType,
    description: raw.description ?? null,
    orderNumber: raw.orderNumber ?? null,
    isB2c: !!raw.isB2c,
    b2cCustomerName: raw.b2cCustomerName ?? null,
    b2cMobileNumber: raw.b2cMobileNumber ?? null,
    media: normalizeMedia(raw.media),
  };
}

export function mapClaim(raw: RawClaim): Claim {
  return {
    id: raw.id,
    status: raw.status,
    source: raw.source,
    salonName: raw.salonName,
    createdByName: raw.createdByName,
    preferredOutcome: raw.preferredOutcome,
    outcome: raw.outcome ?? null,
    productHandling: raw.productHandling ?? null,
    creditNoteNumber: raw.creditNoteNumber ?? null,
    processedByName: raw.processedByName ?? null,
    processedAt: raw.processedAt ?? null,
    createdAt: raw.createdAt,
    items: raw.items.map(mapClaimItem),
  };
}

export function mapProduct(raw: RawProduct): Product {
  return { sku: raw.sku, productName: raw.productName, category: raw.category, imageUrl: raw.imageUrl ?? null };
}

// ---- Multipart builder (exported for tests) ----

/**
 * Bygger multipart/form-data til POST /portal/claims.
 * Nøgleformat (Laravel-stil): items[n][sku], items[n][media][] (filer), …
 * Tomme valgfrie felter udelades. is_b2c sendes som '1'/'0'.
 * Antager hver draft-item har product + claimType sat (valideret i editor).
 */
export function buildClaimFormData(draft: ClaimDraft): FormData {
  const fd = new FormData();
  if (draft.preferredOutcome) fd.append('preferred_outcome', draft.preferredOutcome);

  draft.items.forEach((it, n) => {
    const key = (field: string) => `items[${n}][${field}]`;
    if (it.product) {
      fd.append(key('sku'), it.product.sku);
      fd.append(key('product_name'), it.product.productName);
    }
    fd.append(key('quantity'), String(it.quantity));
    if (it.claimType) fd.append(key('claim_type'), it.claimType);
    if (it.description.trim()) fd.append(key('description'), it.description.trim());
    if (it.orderNumber.trim()) fd.append(key('order_number'), it.orderNumber.trim());
    fd.append(key('is_b2c'), it.isB2c ? '1' : '0');
    if (it.isB2c) {
      if (it.b2cMobileNumber.trim()) fd.append(key('b2c_mobile_number'), it.b2cMobileNumber.trim());
      if (it.b2cCustomerName.trim()) fd.append(key('b2c_customer_name'), it.b2cCustomerName.trim());
    }
    it.media.forEach((f) => fd.append(key('media') + '[]', f));
  });

  return fd;
}

// ---- Endpoints ----

/** GET liste (nyeste først, max 50) for sessionens salon. */
export async function getClaims(): Promise<Claim[]> {
  // Envelope-unwrap i api.ts giver `data` SOM array (kontrakt §3.1), ikke { claims: [...] }.
  const data = await apiGet<RawClaim[]>('/portal/claims');
  return data.map(mapClaim);
}

/** POST create (multipart + Idempotency-Key). Returnerer det oprettede claim hvis serveren sender det. */
export async function createClaim(
  formData: FormData,
  idempotencyKey: string
): Promise<Claim | null> {
  const data = await apiPostForm<{ claim?: RawClaim } | RawClaim>('/portal/claims', {
    formData, idempotencyKey,
  });
  const raw = (data as { claim?: RawClaim }).claim ?? (data as RawClaim);
  return raw && typeof raw.id === 'number' ? mapClaim(raw) : null;
}

/** GET produkt-typeahead (globalt endpoint). */
export async function searchProducts(search: string): Promise<Product[]> {
  const data = await apiGet<RawProduct[]>('/portal/claims/products', {
    query: { search },
  });
  return data.map(mapProduct);
}

/** POST B2C-kundeopslag (salon-scoped; tom liste for en salon uden bookingsystem). */
export async function searchCustomer(phone: string): Promise<CustomerMatch | null> {
  // Svar er et array af matches (kontrakt §3.1); eksakt-opslag → tag første.
  const data = await apiPost<CustomerMatch[]>('/portal/claims/customers/search', {
    body: { phone },
  });
  return data[0] ?? null;
}

/** GET ordre-opslag (soft match, salon-scoped). */
export async function lookupOrder(orderNumber: string): Promise<OrderLookupResult> {
  const data = await apiGet<OrderLookupResult>('/portal/claims/order-lookup', {
    query: { order_number: orderNumber },
  });
  return { matched: !!data.matched, source: data.source ?? null };
}
